import { Tracking } from '@skyscanner/strevda-tracking';
import { extractDestinationExplorer } from '@web-platform/shared-universal-redux/src/state/shared/adverts/combinedExplore/destinationExplorer';
import { extractCombineResultsInlineAdverts } from '@web-platform/shared-universal-redux/src/state/shared/adverts/combinedResults/combinedResultsFlightsInline';
import { extractBookingPanelAdverts } from '@web-platform/shared-universal-redux/src/state/shared/flightBookingPanel/entities/adverts/bookingPanelSponsoredPricingOption';
import {
  ADS_CAPABILITY_EXPLORE,
  ADS_CAPABILITY_HOMEPAGE,
  ADS_RETRIEVAL_SERVICE_BOOKING_PANEL,
  RADAR_CREATE_SESSIONS,
  RADAR_POLL,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { logOperationalEvent } from 'saddlebag-logger';

import { CREATIVES_LOADED } from '../../actions/adverts';

import {
  extractCombinedExploreAdvertsTrackingInfo,
  transformAdsCapabilityExploreToAdvertsTrackingInfo,
  transformAdsCapabilityToAdvertsTrackingInfo,
  transformDestinationExplorerToAdvertTrackingInfo,
} from './utils';

import type { AdvertTrackingInfo } from '@skyscanner/strevda-tracking';
import type { RadarResponsePayload } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarResponsePayloadTypes';
import type {
  AdsCapabilityExplorePayloadResponse,
  AdsCapabilityHomepagePayloadResponse,
  AdsRetrievalServiceBookingPanelPayloadResponse,
} from '@web-platform/shared-universal-types/src/network/types';
import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type { State } from '@web-platform/shared-universal-types/src/redux/banana/state/state-banana-types';
import type { Dispatch, Middleware } from 'redux';

export const advertsLogging: Middleware<{}, State, Dispatch<Action>> =
  (_api) => (next) => (action: Action) => {
    let processedAction: string = 'undefined';
    try {
      let adverts: AdvertTrackingInfo[] = [];
      if (action.type === CREATIVES_LOADED) {
        processedAction = CREATIVES_LOADED;
        adverts = [...adverts, ...transformAdsCapabilityToAdvertsTrackingInfo(action.data)];
      }
      if (
        (action.type === 'QUERY_RESPONSE_RECEIVED' ||
          action.type === 'MUTATION_RESPONSE_RECEIVED') &&
        action.payload?.response
      ) {
        switch (action.payload.type) {
          case RADAR_CREATE_SESSIONS: {
            processedAction = RADAR_CREATE_SESSIONS;
            adverts = [
              ...adverts,
              ...getCombinedResultsAdverts(action.payload.response),
              ...getCombinedExploreAdverts(action.payload.response),
              ...getDestinationExplorerAdverts(action.payload.response),
            ];
            break;
          }
          case RADAR_POLL: {
            processedAction = RADAR_POLL;
            adverts = [...adverts, ...getCombinedResultsAdverts(action.payload.response)];
            break;
          }
          case ADS_CAPABILITY_HOMEPAGE: {
            processedAction = ADS_CAPABILITY_HOMEPAGE;
            adverts = [...adverts, ...getAdsCapabilityHomepageAdverts(action.payload.response)];
            break;
          }
          case ADS_CAPABILITY_EXPLORE: {
            processedAction = ADS_CAPABILITY_EXPLORE;
            adverts = [...adverts, ...getAdsCapabilityExploreAdverts(action.payload.response)];
            break;
          }
          case ADS_RETRIEVAL_SERVICE_BOOKING_PANEL: {
            processedAction = ADS_RETRIEVAL_SERVICE_BOOKING_PANEL;
            adverts = [...adverts, ...getBookingPanelAdverts(action.payload.response)];
            break;
          }
        }
      }
      logAdverts(adverts);
    } catch (error) {
      logOperationalEvent({
        eventName: 'adverts_logging_error',
        error: String(error),
        errorType: 'Error while attempting to log ad received',
        processedAction,
      });
    }
    return next(action);
  };

const logAdverts = (advertsTrackingInfo: AdvertTrackingInfo[]): void => {
  if (advertsTrackingInfo) {
    advertsTrackingInfo.forEach((advert) => {
      Tracking.trackAdReceived(advert);
    });
  }
};

const getAdsCapabilityHomepageAdverts = (
  response: AdsCapabilityHomepagePayloadResponse,
): AdvertTrackingInfo[] => {
  if (response.error) return [];
  return transformAdsCapabilityToAdvertsTrackingInfo(response.body);
};

const getCombinedResultsAdverts = (response: RadarResponsePayload): AdvertTrackingInfo[] => {
  const combineResultsInlineAdverts = extractCombineResultsInlineAdverts(response);
  return combineResultsInlineAdverts || [];
};

const getCombinedExploreAdverts = (response: RadarResponsePayload): AdvertTrackingInfo[] => {
  const combineExploreAdverts = extractCombinedExploreAdvertsTrackingInfo(response);
  return combineExploreAdverts || [];
};

const getDestinationExplorerAdverts = (response: RadarResponsePayload): AdvertTrackingInfo[] => {
  const destinationExplorerAdvert = extractDestinationExplorer(response);
  return destinationExplorerAdvert
    ? [transformDestinationExplorerToAdvertTrackingInfo(destinationExplorerAdvert)]
    : [];
};

const getBookingPanelAdverts = (
  response: AdsRetrievalServiceBookingPanelPayloadResponse,
): AdvertTrackingInfo[] => {
  const bookingPanelAdvert = extractBookingPanelAdverts(response);
  return bookingPanelAdvert ? [bookingPanelAdvert] : [];
};

const getAdsCapabilityExploreAdverts = (
  response: AdsCapabilityExplorePayloadResponse,
): AdvertTrackingInfo[] => {
  if (response.error) return [];
  return transformAdsCapabilityExploreToAdvertsTrackingInfo(response.body);
};
